
import { consoleLog } from "./consoleLog/consoleLog";

const STORAGE_KEY = "_zenvoya_search_currency";

let current: string | null | undefined;

const normalize = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const code = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : undefined;
};

const readStorage = (): string | null => {
  try {
    return normalize(window.sessionStorage.getItem(STORAGE_KEY)) ?? null;
  } catch {
    return null;
  }
};

export const getSearchCurrency = (): string | undefined => {
  if (current === undefined) {
    current = typeof window === "undefined" ? null : readStorage();
  }
  return current ?? undefined;
};

export const recordSearchCurrency = (...candidates: unknown[]): void => {
  let code: string | undefined;
  for (const candidate of candidates) {
    code = normalize(candidate);
    if (code) break;
  }
  const previous = getSearchCurrency();
  if (!code || code === previous) return;

  consoleLog(["SEARCH CURRENCY", code, previous ? `(was ${previous})` : "(first)"]);

  current = code;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, code);
  } catch {
  }
};

export const clearSearchCurrency = (): void => {
  current = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
  }
};

export const recordCurrencyFromHotelSearch = (response: any): void => {
  const data = response?.data ?? response;
  const cities: any[] = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
  const candidates: unknown[] = [];
  for (const city of cities) {
    const hotels: any[] = Array.isArray(city?.hotels) ? city.hotels : [];
    for (const hotel of hotels) {
      candidates.push(hotel?.currency, hotel?.totalPrice?.currency, hotel?.pricePerRoom?.currency);
    }
    candidates.push(city?.facets?.priceRange?.currency);
  }
  candidates.push(data?.facets?.priceRange?.currency);
  recordSearchCurrency(...candidates);
};

export const recordCurrencyFromRoomPrices = (response: any): void => {
  const rooms: any[] = Array.isArray(response?.rooms)
    ? response.rooms
    : Array.isArray(response?.data?.rooms)
      ? response.data.rooms
      : [];
  const candidates: unknown[] = [];
  for (const room of rooms) {
    const rates: any[] = Array.isArray(room?.roomPrices) ? room.roomPrices : [];
    for (const rate of rates) candidates.push(rate?.currency);
  }
  recordSearchCurrency(...candidates);
};

export const recordCurrencyFromFlightSearch = (response: any): void => {
  const data = response?.data ?? response;
  const sectors: any[] = Array.isArray(data?.sectors) ? data.sectors : Array.isArray(data) ? data : [];
  const candidates: unknown[] = [];
  for (const sector of sectors) candidates.push(sector?.currency, sector?.price?.currency);
  candidates.push(data?.filtersAvailable?.price?.currency);
  recordSearchCurrency(...candidates);
};

export const recordCurrencyFromPriceReview = (response: any): void => {
  const data = response?.data ?? response;
  const hotels: any[] = Array.isArray(data?.fareBreakdown?.hotels) ? data.fareBreakdown.hotels : [];
  recordSearchCurrency(
    data?.price?.currency,
    data?.fareDetails?.hotel?.currency,
    data?.fareDetails?.flight?.currency,
    data?.fareBreakdown?.flights?.currency,
    ...hotels.map((hotel: any) => hotel?.currency),
  );
};

export const recordCurrencyFromHotelAvailability = (response: any): void => {
  const data = response?.data ?? response;
  const entries: any[] = Array.isArray(data) ? data : Array.isArray(data?.hotels) ? data.hotels : [];
  const candidates: unknown[] = [];
  for (const entry of entries) {
    candidates.push(
      entry?.pricing?.currency,
      entry?.pricing?.totalPrice?.currency,
      entry?.currency,
    );
  }
  recordSearchCurrency(...candidates);
};

export const getPaymentCurrency = (...candidates: unknown[]): string => {
  for (const candidate of candidates) {
    const code = normalize(candidate);
    if (code) return code;
  }
  return getSearchCurrency() || "USD";
};
